import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import useNotificationStore from "../../Zustand/Store/useNotificationStore";
import { MAIN_URL } from "../../Configurations/Urls";
import {
  applyLeadContactTimingIstFields,
  inferPreferredTimezoneFromLead,
  isValidIanaTimezone,
  normalizePreferredContactTimezone,
} from "../../Utils/DateAndTime";

const convertToLaravelDateTime = (input) => {
  if (!input) return null;
  const date = new Date(input);
  const pad = (n) => String(n).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

function refreshNotificationUnreadAfterLeadAssign(baseUrl, organizationId, formPayload) {
  if (organizationId == null || String(organizationId).trim() === "") return;
  const orgNum = Number(organizationId);
  if (!Number.isFinite(orgNum) || orgNum <= 0) return;
  const shouldRefresh =
    baseUrl === "/api/ems/lead-activities" ||
    (baseUrl === "/api/ems/leads" && formPayload?.lead_activity);
  if (shouldRefresh) {
    useNotificationStore.getState().loadUnreadCount(orgNum);
  }
}

function toastLeadAssigneeNotified(responseData, formPayload, userData) {
  if (!responseData?.assignee_notified) return;
  const assigneeOrgUserId =
    formPayload?.organization_user_id ??
    formPayload?.lead_activity?.organization_user_id ??
    null;
  const currentOrgUserId =
    userData?.organizationuser?.organization_user_id ??
    userData?.organization_user_id ??
    null;
  if (
    assigneeOrgUserId != null &&
    currentOrgUserId != null &&
    Number(assigneeOrgUserId) === Number(currentOrgUserId)
  ) {
    toast.success("You have a new lead assignment notification.");
    return;
  }
  toast.success("The selected Activity By user will be notified.");
}

/** API `Y-m-d H:i:s` / ISO → value for `<input type="datetime-local">`. */
const apiDatetimeToDatetimeLocal = (v) => {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  if (s.includes("T")) return s.slice(0, 16);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  if (m) return `${m[1]}T${m[2]}`;
  return s;
};

/** Value for HTML `datetime-local` inputs (local date + time, no seconds). */
const toDatetimeLocalInputValue = (d = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * API often returns `HH:mm:ss`; `<input type="time">` and form validation expect `HH:mm`.
 * Also normalizes `h:mm AM/PM` if it ever appears in state.
 */
const normalizeBatchTimeForInput = (value) => {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  const m24 = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (m24) {
    const h = Math.min(23, Math.max(0, parseInt(m24[1], 10)));
    const min = Math.min(59, Math.max(0, parseInt(m24[2], 10)));
    return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
  }
  const m12 = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const min = Math.min(59, Math.max(0, parseInt(m12[2], 10)));
    const ap = m12[3].toUpperCase();
    if (ap === "PM" && h !== 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    h = Math.min(23, Math.max(0, h));
    return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
  }
  return s;
};

export default function useFormHandler({
  baseUrl,
  initialData,
  validateForm,
  successMessages,
  redirectPath,
  fetchDependencies = [],
  /** Optional: run after successful POST in add mode (receives created resource). */
  afterSuccessfulCreate,
  /** Optional: run after successful PUT in edit mode (after main resource is updated). */
  afterSuccessfulUpdate,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isViewMode = location.pathname.includes("/view/");
  const mode = isViewMode ? "view" : id ? "edit" : "add";
  const { userData } = useAuthStore();
  const organizationId =
    userData?.organization?.organization_id ??
    userData?.organization_id ??
    userData?.user_active_organization?.organization_id ??
    null;

  const [formData, setFormData] = useState(initialData);

  useEffect(() => {
    if (mode !== "add") return;

    setFormData((prev) => {
      const orgId =
        userData?.organization?.organization_id ??
        userData?.organization_id ??
        userData?.user_active_organization?.organization_id ??
        "";

      const merged = {
        ...initialData,
        ...prev,
        organization_id: orgId || prev.organization_id || "",
      };

      if (
        baseUrl === "/api/ems/admissions" &&
        (merged.admission_date === "" ||
          merged.admission_date === undefined ||
          merged.admission_date === null)
      ) {
        merged.admission_date = new Date().toISOString().slice(0, 10);
      }
      if (
        baseUrl === "/api/ems/leads" &&
        (merged.lead_datetime === "" ||
          merged.lead_datetime === undefined ||
          merged.lead_datetime === null)
      ) {
        merged.lead_datetime = toDatetimeLocalInputValue(new Date());
      }
      if (
        baseUrl === "/api/ems/lead-activities" &&
        (merged.activity_datetime === "" ||
          merged.activity_datetime === undefined ||
          merged.activity_datetime === null)
      ) {
        merged.activity_datetime = toDatetimeLocalInputValue(new Date());
      }
      if (
        baseUrl === "/api/ems/students-fees" &&
        (merged.payment_date === "" ||
          merged.payment_date === undefined ||
          merged.payment_date === null)
      ) {
        merged.payment_date = new Date().toISOString().slice(0, 10);
      }

      return merged;
    });
  }, [initialData, userData?.organization?.organization_id, baseUrl, mode]);

  // Prefill add-form fields from query params (used by Fee Installments -> Fee Receipts "Receipt" button).
  useEffect(() => {
    if (mode !== "add") return;

    const params = new URLSearchParams(location.search);
    const updates = {};

    // Fee Receipts prefill: Fee Installments -> Fee Receipts "Receipt" button
    const studentId = params.get("organization_ems_student_id");
    const admissionId = params.get("organization_ems_admission_id");
    const feeInstallmentId = params.get("organization_ems_fee_installment_id");
    const studentCurrencyCode = params.get("student_currency_code");
    const amountReceivedInr = params.get("amount_received_inr");
    const amountPaidStudentCurrency = params.get("amount_paid_student_currency");
    const paymentDateParam = params.get("payment_date");

    if (studentId) updates.organization_ems_student_id = studentId;
    if (admissionId) updates.organization_ems_admission_id = admissionId;
    if (feeInstallmentId) {
      updates.organization_ems_fee_installment_id = feeInstallmentId;
    }
    if (studentCurrencyCode)
      updates.student_currency_code = studentCurrencyCode;
    if (baseUrl === "/api/ems/students-fees") {
      if (
        paymentDateParam != null &&
        String(paymentDateParam).trim() !== "" &&
        String(paymentDateParam).trim() !== "-"
      ) {
        updates.payment_date = String(paymentDateParam).trim().slice(0, 10);
      }
      if (
        amountReceivedInr != null &&
        String(amountReceivedInr).trim() !== "" &&
        String(amountReceivedInr).trim() !== "-"
      ) {
        updates.amount_received_inr = amountReceivedInr;
      }
      if (
        amountPaidStudentCurrency != null &&
        String(amountPaidStudentCurrency).trim() !== "" &&
        String(amountPaidStudentCurrency).trim() !== "-"
      ) {
        updates.amount_paid_student_currency = amountPaidStudentCurrency;
      }
    }

    // Students prefill: Leads -> Students "Enrolled" button
    if (baseUrl === "/api/ems/students") {
      const keys = [
        "organization_ems_lead_id",
        "country_id",
        "state_id",
        "city_id",
        "state_name",
        "city_name",
        "first_name",
        "last_name",
        "gender",
        "date_of_birth",
        "email",
        "phone",
        "alternate_phone",
        "address",
        "certificate_name",
        "remarks",
      ];

      keys.forEach((k) => {
        const v = params.get(k);
        if (v && v !== "-") updates[k] = v;
      });
    }

    // Admissions prefill: Students -> Admissions "Admission" button
    if (baseUrl === "/api/ems/admissions") {
      const studentId = params.get("organization_ems_student_id");
      const trainingProgramId = params.get("training_program_id");

      if (studentId) updates.organization_ems_student_id = studentId;
      if (trainingProgramId) updates.training_program_id = trainingProgramId;
    }

    if (baseUrl === "/api/ems/demo-participants") {
      const demoSessionId = params.get("organization_ems_demo_session_id");
      const leadId = params.get("organization_ems_lead_id");
      if (demoSessionId) updates.organization_ems_demo_session_id = demoSessionId;
      if (leadId) updates.organization_ems_lead_id = leadId;
    }

    // Leads list -> Lead Activities form: prefill lead when from_leads_list=1
    if (baseUrl === "/api/ems/lead-activities") {
      const leadActivityLeadId = params.get("organization_ems_lead_id");
      if (
        leadActivityLeadId != null &&
        String(leadActivityLeadId).trim() !== "" &&
        String(leadActivityLeadId).trim() !== "-"
      ) {
        updates.organization_ems_lead_id = String(leadActivityLeadId).trim();
      }
    }

    // Batches list -> Batch Classes add form: prefill selected batch
    if (baseUrl === "/api/ems/batch-classes") {
      const batchId = params.get("organization_ems_batch_id");
      if (
        batchId != null &&
        String(batchId).trim() !== "" &&
        String(batchId).trim() !== "-"
      ) {
        updates.organization_ems_batch_id = String(batchId).trim();
      }
    }
    // Batches list -> Batch Students add form: prefill selected batch
    if (baseUrl === "/api/ems/batch-students") {
      const batchId = params.get("organization_ems_batch_id");
      if (
        batchId != null &&
        String(batchId).trim() !== "" &&
        String(batchId).trim() !== "-"
      ) {
        updates.organization_ems_batch_id = String(batchId).trim();
      }
    }
    // Batch Classes list -> Class Records add form: prefill batch + batch class
    if (baseUrl === "/api/ems/class-records") {
      const batchId = params.get("organization_ems_batch_id");
      if (
        batchId != null &&
        String(batchId).trim() !== "" &&
        String(batchId).trim() !== "-"
      ) {
        updates.organization_ems_batch_id = String(batchId).trim();
      }
    }

    if (Object.keys(updates).length > 0) {
      setFormData((prev) => ({ ...prev, ...updates }));
    }
  }, [mode, location.search, baseUrl]);

  // Class Records add flow: set batch class only after batch is present in form state.
  useEffect(() => {
    if (mode !== "add") return;
    if (baseUrl !== "/api/ems/class-records") return;

    const params = new URLSearchParams(location.search);
    const batchId = params.get("organization_ems_batch_id");
    const batchClassId = params.get("organization_ems_batch_class_id");
    const hasBatch =
      batchId != null &&
      String(batchId).trim() !== "" &&
      String(batchId).trim() !== "-";
    const hasBatchClass =
      batchClassId != null &&
      String(batchClassId).trim() !== "" &&
      String(batchClassId).trim() !== "-";
    if (!hasBatch || !hasBatchClass) return;

    if (String(formData?.organization_ems_batch_id ?? "").trim() !== String(batchId).trim()) {
      return;
    }
    if (
      String(formData?.organization_ems_batch_class_id ?? "").trim() ===
      String(batchClassId).trim()
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      organization_ems_batch_class_id: String(batchClassId).trim(),
    }));
  }, [
    mode,
    baseUrl,
    location.search,
    formData?.organization_ems_batch_id,
    formData?.organization_ems_batch_class_id,
  ]);

  // Robust prefill for Class Records add: if query has only batch class id,
  // fetch batch-class details and derive both batch + batch class fields.
  useEffect(() => {
    if (mode !== "add") return;
    if (baseUrl !== "/api/ems/class-records") return;

    const params = new URLSearchParams(location.search);
    const batchIdParam = params.get("organization_ems_batch_id");
    const batchClassIdParam = params.get("organization_ems_batch_class_id");
    const hasBatchClassParam =
      batchClassIdParam != null &&
      String(batchClassIdParam).trim() !== "" &&
      String(batchClassIdParam).trim() !== "-";
    if (!hasBatchClassParam) return;

    const hasBatchInForm =
      formData?.organization_ems_batch_id != null &&
      String(formData.organization_ems_batch_id).trim() !== "" &&
      String(formData.organization_ems_batch_id).trim() !== "-";
    const hasBatchClassInForm =
      formData?.organization_ems_batch_class_id != null &&
      String(formData.organization_ems_batch_class_id).trim() !== "" &&
      String(formData.organization_ems_batch_class_id).trim() !== "-";
    if (hasBatchInForm && hasBatchClassInForm) return;

    // If both params are already present, no fetch required.
    const hasBatchParam =
      batchIdParam != null &&
      String(batchIdParam).trim() !== "" &&
      String(batchIdParam).trim() !== "-";
    if (hasBatchParam) return;

    let cancelled = false;
    const token = localStorage.getItem("token");
    (async () => {
      try {
        const classId = String(batchClassIdParam).trim();
        const res = await axios.get(`${MAIN_URL}/api/ems/batch-classes/${classId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const payload = res.data?.data ?? res.data;
        const row = payload?.data ?? payload;
        const derivedBatchId =
          row?.organization_ems_batch_id ??
          row?.batch?.organization_ems_batch_id ??
          "";
        if (cancelled) return;
        setFormData((prev) => ({
          ...prev,
          organization_ems_batch_id:
            prev?.organization_ems_batch_id &&
            String(prev.organization_ems_batch_id).trim() !== ""
              ? prev.organization_ems_batch_id
              : derivedBatchId
                ? String(derivedBatchId)
                : prev?.organization_ems_batch_id,
          organization_ems_batch_class_id:
            prev?.organization_ems_batch_class_id &&
            String(prev.organization_ems_batch_class_id).trim() !== ""
              ? prev.organization_ems_batch_class_id
              : classId,
        }));
      } catch (err) {
        // Non-blocking fallback; form remains editable manually.
        console.error("Failed to prefill class record batch from batch class:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    mode,
    baseUrl,
    location.search,
    formData?.organization_ems_batch_id,
    formData?.organization_ems_batch_class_id,
  ]);

  // Students -> Admissions: if TrainingProgram ID isn't in query params,
  // fetch student details and derive training_program_id to prefill the admission form.
  useEffect(() => {
    if (mode !== "add") return;
    if (baseUrl !== "/api/ems/admissions") return;

    const params = new URLSearchParams(location.search);
    const studentId = params.get("organization_ems_student_id");
    const trainingProgramIdFromParams = params.get("training_program_id");

    const hasValid = (v) => {
      if (v === undefined || v === null) return false;
      const s = String(v).trim();
      return s !== "" && s !== "-";
    };

    if (!hasValid(studentId)) return;
    const trainingMissing = !hasValid(trainingProgramIdFromParams);

    if (!trainingMissing) return;

    const token = localStorage.getItem("token");
    const fetchStudent = async () => {
      try {
        const res = await axios.get(`${MAIN_URL}/api/ems/students/${studentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const student = res.data?.data || res.data || {};

        const derivedTrainingProgramId =
          student.training_program_id ??
          student.organization_ems_training_program_id ??
          student.training_program?.organization_ems_training_program_id ??
          student.training_program?.training_program_id ??
          "";

        setFormData((prev) => ({
          ...prev,
          ...(trainingMissing && derivedTrainingProgramId
            ? { training_program_id: derivedTrainingProgramId }
            : null),
        }));
      } catch (err) {
        // Non-blocking: admissions form can still be filled manually
        console.error("Failed to prefill admissions from student:", err);
      }
    };

    fetchStudent();
  }, [mode, baseUrl, location.search]);

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  const fetchData = async () => {
    if (mode === "add") return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${MAIN_URL}${baseUrl}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.data) {
        const responseData = res.data.data;
        if (baseUrl === "/api/ems/training-programs") {
          const normalizedFeeStructures = Array.isArray(
            responseData?.fee_structures
          )
            ? responseData.fee_structures
            : responseData?.fee_structure
              ? [responseData.fee_structure]
              : [];
          setFormData({
            ...responseData,
            fee_structures: normalizedFeeStructures.map((row) => ({
              currency_code: row?.currency_code || "",
              currency_name: row?.currency_name || "",
              amount:
                row?.amount === null || row?.amount === undefined
                  ? ""
                  : String(row.amount),
            })),
          });
        } else if (baseUrl === "/api/ems/batch-trainers" && responseData) {
          const tid = responseData.trainer_employee_id;
          const tr = responseData.trainer;
          setFormData({
            ...responseData,
            trainer:
              tr && tid != null && tr.organization_user_id == null
                ? { ...tr, organization_user_id: tid }
                : tr,
          });
        } else if (baseUrl === "/api/ems/lead-activities" && responseData) {
          const aid = responseData.organization_user_id;
          const tr = responseData.trainer;
          setFormData({
            ...responseData,
            organization_user_id:
              aid ??
              tr?.organization_user_id ??
              tr?.organization_user?.organization_user_id ??
              responseData.organization_person_id ??
              "",
          });
        } else if (baseUrl === "/api/ems/demo-sessions" && responseData) {
          const dp =
            responseData.demo_participants ??
            responseData.participants ??
            responseData.demoParticipants;
          setFormData({
            ...responseData,
            demo_participants: Array.isArray(dp) ? dp : [],
          });
        } else if (baseUrl === "/api/ems/batches" && responseData) {
          setFormData({
            ...responseData,
            batch_start_time: normalizeBatchTimeForInput(
              responseData.batch_start_time
            ),
            batch_end_time: normalizeBatchTimeForInput(
              responseData.batch_end_time
            ),
          });
        } else if (baseUrl === "/api/ems/batch-classes" && responseData) {
          const rawMode =
            responseData.mode ??
            responseData.class_mode ??
            responseData.batch_mode ??
            responseData.batch?.batch_mode ??
            "";
          const modeStr = rawMode === null || rawMode === undefined ? "" : String(rawMode).trim();
          const modeNormalized =
            modeStr === ""
              ? ""
              : ["Online", "Offline"].includes(modeStr)
                ? modeStr
                : modeStr.toLowerCase() === "online"
                  ? "Online"
                  : modeStr.toLowerCase() === "offline"
                    ? "Offline"
                    : modeStr;

          const trainingSpaceId =
            responseData.ems_training_space_id ??
            responseData.organization_ems_training_space_id ??
            responseData.training_space_id ??
            responseData.training_space?.ems_training_space_id ??
            responseData.training_space?.organization_ems_training_space_id ??
            responseData.training_space?.training_space_id ??
            responseData.TrainingSpace?.ems_training_space_id ??
            responseData.TrainingSpace?.organization_ems_training_space_id ??
            responseData.TrainingSpace?.training_space_id ??
            responseData.batchClass?.ems_training_space_id ??
            responseData.batchClass?.organization_ems_training_space_id ??
            responseData.batchClass?.training_space_id ??
            responseData.batch_class?.ems_training_space_id ??
            responseData.batch_class?.organization_ems_training_space_id ??
            responseData.batch_class?.training_space_id ??
            responseData.batchClass?.training_space?.ems_training_space_id ??
            responseData.batchClass?.training_space?.organization_ems_training_space_id ??
            responseData.batchClass?.TrainingSpace?.ems_training_space_id ??
            responseData.batchClass?.TrainingSpace?.organization_ems_training_space_id ??
            responseData.batch_class?.training_space?.ems_training_space_id ??
            responseData.batch_class?.training_space?.organization_ems_training_space_id ??
            responseData.batch_class?.TrainingSpace?.ems_training_space_id ??
            responseData.batch_class?.TrainingSpace?.organization_ems_training_space_id ??
            "";

          const { organization_ems_training_space_id: _tsOmit, ...restBatchClass } =
            responseData;

          setFormData({
            ...restBatchClass,
            ems_training_space_id:
              trainingSpaceId === "" || trainingSpaceId == null
                ? ""
                : String(trainingSpaceId),
            mode: modeNormalized,
            start_time: normalizeBatchTimeForInput(responseData.start_time),
            end_time: normalizeBatchTimeForInput(responseData.end_time),
          });
        } else if (baseUrl === "/api/ems/class-records" && responseData) {
          const normalizedBatchId =
            responseData.organization_ems_batch_id ??
            responseData.batch?.organization_ems_batch_id ??
            responseData.batchClass?.organization_ems_batch_id ??
            responseData.batch_class?.organization_ems_batch_id ??
            "";
          const normalizedBatchClassId =
            responseData.organization_ems_batch_class_id ??
            responseData.batchClass?.organization_ems_batch_class_id ??
            responseData.batch_class?.organization_ems_batch_class_id ??
            "";
          const normalizedTrainingSpaceId =
            responseData.ems_training_space_id ??
            responseData.organization_ems_training_space_id ??
            responseData.training_space?.ems_training_space_id ??
            responseData.training_space?.organization_ems_training_space_id ??
            responseData.batchClass?.ems_training_space_id ??
            responseData.batchClass?.organization_ems_training_space_id ??
            responseData.batch_class?.ems_training_space_id ??
            responseData.batch_class?.organization_ems_training_space_id ??
            "";
          const normalizedMode =
            responseData.mode ??
            responseData.class_mode ??
            responseData.batchClass?.mode ??
            responseData.batch_class?.mode ??
            "";
          setFormData({
            ...responseData,
            organization_ems_batch_id: normalizedBatchId,
            organization_ems_batch_class_id: normalizedBatchClassId,
            ems_training_space_id: normalizedTrainingSpaceId,
            mode: normalizedMode,
            start_time: apiDatetimeToDatetimeLocal(responseData.start_time),
            end_time: apiDatetimeToDatetimeLocal(responseData.end_time),
          });
        } else if (baseUrl === "/api/ems/batch-students" && responseData) {
          const rawMode =
            responseData.mode ??
            responseData.batch_mode ??
            responseData.batch?.mode ??
            responseData.batch?.batch_mode ??
            "";
          const modeStr =
            rawMode === null || rawMode === undefined
              ? ""
              : String(rawMode).trim();
          const modeNormalized =
            modeStr === ""
              ? ""
              : ["Online", "Offline"].includes(modeStr)
                ? modeStr
                : modeStr.toLowerCase() === "online"
                  ? "Online"
                  : modeStr.toLowerCase() === "offline"
                    ? "Offline"
                    : modeStr;
          setFormData({
            ...responseData,
            mode: modeNormalized,
          });
        } else if (baseUrl === "/api/ems/leads" && responseData) {
          const activities = Array.isArray(responseData.lead_activities)
            ? responseData.lead_activities
            : Array.isArray(responseData.leadActivities)
              ? responseData.leadActivities
              : [];
          setFormData({
            ...responseData,
            lead_activities: activities,
            lead_datetime: apiDatetimeToDatetimeLocal(responseData.lead_datetime),
          });
        } else if (baseUrl === "/api/ems/lead-activities" && responseData) {
          setFormData({
            ...responseData,
            activity_datetime: apiDatetimeToDatetimeLocal(
              responseData.activity_datetime
            ),
            _lead_datetime: apiDatetimeToDatetimeLocal(
              responseData.lead?.lead_datetime ?? responseData.lead_datetime
            ),
          });
        } else if (baseUrl === "/api/ems/lead-contact-timings" && responseData) {
          const normTime = (v) => normalizeBatchTimeForInput(v);
          setFormData({
            ...responseData,
            organization_id:
              responseData.organization_id ??
              organizationId ??
              "",
            preferred_contact_timezone: normalizePreferredContactTimezone(
              responseData.preferred_contact_timezone
            ),
            morning_student_time_start: normTime(
              responseData.morning_student_time_start
            ),
            morning_student_time_end: normTime(
              responseData.morning_student_time_end
            ),
            evening_student_time_start: normTime(
              responseData.evening_student_time_start
            ),
            evening_student_time_end: normTime(
              responseData.evening_student_time_end
            ),
            morning_ist_time_start: normTime(responseData.morning_ist_time_start),
            morning_ist_time_end: normTime(responseData.morning_ist_time_end),
            evening_ist_time_start: normTime(
              responseData.evening_ist_time_start
            ),
            evening_ist_time_end: normTime(responseData.evening_ist_time_end),
          });
        } else {
          setFormData(responseData);
        }
      }
    } catch (err) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, ...fetchDependencies]);

  // ========== HANDLERS ==========
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      let next = { ...prev, [name]: value };

      if (name === "country_id") {
        next = { ...next, state_id: "", city_id: "" };
      }
      if (name === "state_id") {
        next = { ...next, city_id: "" };
      }

      if (baseUrl === "/api/ems/class-records" && name === "organization_ems_batch_id") {
        next = { ...next, organization_ems_batch_class_id: "" };
      }
      if (
        baseUrl === "/api/ems/classes-attendance" &&
        name === "organization_ems_batch_class_id"
      ) {
        next = {
          ...next,
          organization_ems_batch_class_record_id: "",
          organization_ems_student_id: "",
        };
      }
      if (baseUrl === "/api/ems/class-records" && name === "status") {
        const normalizedStatus = String(value ?? "").trim();
        if (normalizedStatus !== "Cancelled") {
          next = { ...next, cancel_reason: "" };
        }
      }

      if (
        baseUrl === "/api/ems/students" &&
        (name === "first_name" || name === "last_name")
      ) {
        const fn = name === "first_name" ? value : prev.first_name;
        const ln = name === "last_name" ? value : prev.last_name;
        next.certificate_name = [fn, ln]
          .filter((s) => s != null && String(s).trim() !== "")
          .map((s) => String(s).trim())
          .join(" ");
      }

      if (baseUrl === "/api/ems/lead-contact-timings") {
        const istRecalcKeys = new Set([
          "preferred_contact_timezone",
          "morning_student_time_start",
          "morning_student_time_end",
          "evening_student_time_start",
          "evening_student_time_end",
        ]);
        if (istRecalcKeys.has(name)) {
          next.preferred_contact_timezone = normalizePreferredContactTimezone(
            next.preferred_contact_timezone
          );
          if (
            next.preferred_contact_timezone &&
            isValidIanaTimezone(next.preferred_contact_timezone)
          ) {
            const draft = { ...next };
            applyLeadContactTimingIstFields(draft);
            next = {
              ...next,
              ...draft,
              morning_student_time_start: normalizeBatchTimeForInput(
                draft.morning_student_time_start
              ),
              morning_student_time_end: normalizeBatchTimeForInput(
                draft.morning_student_time_end
              ),
              evening_student_time_start: normalizeBatchTimeForInput(
                draft.evening_student_time_start
              ),
              evening_student_time_end: normalizeBatchTimeForInput(
                draft.evening_student_time_end
              ),
              morning_ist_time_start: normalizeBatchTimeForInput(
                draft.morning_ist_time_start
              ),
              morning_ist_time_end: normalizeBatchTimeForInput(
                draft.morning_ist_time_end
              ),
              evening_ist_time_start: normalizeBatchTimeForInput(
                draft.evening_ist_time_start
              ),
              evening_ist_time_end: normalizeBatchTimeForInput(
                draft.evening_ist_time_end
              ),
            };
          }
        }
      }

      return next;
    });

    setFormErrors((prev) => {
      const next = { ...prev, [name]: "" };
      if (name === "country_id") {
        next.state_id = "";
        next.city_id = "";
      }
      if (name === "state_id") {
        next.city_id = "";
      }
      return next;
    });
  };

  const handleChangeImage = (key, file) => {
    setFormData((prev) => ({ ...prev, [key]: file }));
    setFormErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleFileChange = (e, key) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, [key]: file }));
      setFormErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const handleRemoveFile = (key) => {
    setFormData((prev) => ({ ...prev, [key]: null }));
  };

  const handlePreview = (file) => {
    const url = file instanceof File ? URL.createObjectURL(file) : `${file}`;
    window.open(url, "_blank");
  };

  const handleSubmit = async () => {
    const dataForValidation = {
      ...formData,
      organization_id:
        formData.organization_id ??
        organizationId ??
        "",
    };
    if (!validateForm(dataForValidation, setFormErrors)) {
      toast.error("Please fix the validation errors.");
      return;
    }

    const token = localStorage.getItem("token");
    const organization_entity_id =
      userData?.organization?.entities?.organization_entity_id || null;

    setBtnLoading(true);

    try {
      const processedFormData = Object.entries(formData).reduce(
        (acc, [key, value]) => {
          if (key.endsWith("_datetime") && value) {
            acc[key] = convertToLaravelDateTime(value);
          } else {
            acc[key] = value;
          }
          return acc;
        },
        {}
      );

      // Convert _remove_<key>: "1" flags (set when user removes a Picture field) to remove_<key>: "1"
      Object.keys(processedFormData).forEach((key) => {
        if (key.startsWith("_remove_") && processedFormData[key] === "1") {
          processedFormData[key.slice(1)] = "1"; // strip leading underscore → remove_<key>
          delete processedFormData[key];
        }
      });

      if (baseUrl === "/api/ems/certificates") {
        delete processedFormData._certificate_eligible;
        delete processedFormData._admission_has_certificate;
        delete processedFormData.certificate_number;
      }

      if (baseUrl === "/api/ems/training-programs") {
        ["duration_in_hours", "duration_in_months"].forEach((key) => {
          if (
            processedFormData[key] === "" ||
            processedFormData[key] === null ||
            processedFormData[key] === undefined
          ) {
            delete processedFormData[key];
          }
        });
        processedFormData.fee_structures = (
          Array.isArray(processedFormData.fee_structures)
            ? processedFormData.fee_structures
            : []
        ).map((row) => ({
          currency_code: row?.currency_code,
          amount: row?.amount,
        }));
        delete processedFormData.amount;
        delete processedFormData.currency_code;
        delete processedFormData.fee_structure;
      }
      if (baseUrl === "/api/ems/fee-installments") {
        processedFormData.currency_code_inr =
          processedFormData.currency_code_inr || "INR";
        delete processedFormData.existing_fee_installments;
        delete processedFormData._admission_net_fee_inr;
        delete processedFormData._admission_net_fee_student;
        delete processedFormData._admission_installment_count;
        delete processedFormData._initial_status;
        delete processedFormData._paid_fee_receipt_installment_ids;
        delete processedFormData._form_mode;
      }

      if (baseUrl === "/api/ems/admissions") {
        const count = Number(processedFormData.installment_count || 0);
        const sourceRows = Array.isArray(processedFormData.fee_installments)
          ? processedFormData.fee_installments
          : [];
        const normalizedRows = Array.from(
          { length: Math.max(count, sourceRows.length) },
          (_, idx) => {
            const row = sourceRows[idx] || {};
            return {
              installment_number: Number(row?.installment_number || idx + 1),
              due_date: row?.due_date || null,
              fee_instalment_inr:
                row?.fee_instalment_inr === "" ||
                row?.fee_instalment_inr === null ||
                row?.fee_instalment_inr === undefined
                  ? null
                  : Number(row.fee_instalment_inr),
              currency_code_inr: row?.currency_code_inr || "INR",
              student_currency_code:
                row?.student_currency_code || processedFormData.currency_code || null,
              fee_instalment_student_student_currency:
                row?.fee_instalment_student_student_currency === "" ||
                row?.fee_instalment_student_student_currency === null ||
                row?.fee_instalment_student_student_currency === undefined
                  ? null
                  : Number(row.fee_instalment_student_student_currency),
            };
          }
        );

        processedFormData.fee_installments = normalizedRows;
        // Extra alias in case backend expects this naming.
        processedFormData.no_of_installments = count || normalizedRows.length;
      }

      if (baseUrl === "/api/ems/class-records") {
        const tsRaw =
          processedFormData.ems_training_space_id !== "" &&
          processedFormData.ems_training_space_id != null &&
          processedFormData.ems_training_space_id !== undefined
            ? processedFormData.ems_training_space_id
            : processedFormData.organization_ems_training_space_id;
        delete processedFormData.organization_ems_training_space_id;
        if (tsRaw !== "" && tsRaw != null && tsRaw !== undefined) {
          processedFormData.ems_training_space_id = Number(tsRaw);
        } else {
          delete processedFormData.ems_training_space_id;
        }
        ["mode", "topic", "recording_url", "cancel_reason"].forEach((key) => {
          if (processedFormData[key] === "") {
            delete processedFormData[key];
          }
        });
        if (processedFormData.start_time) {
          processedFormData.start_time = convertToLaravelDateTime(
            processedFormData.start_time
          );
        }
        if (processedFormData.end_time) {
          processedFormData.end_time = convertToLaravelDateTime(
            processedFormData.end_time
          );
        }
        if (
          processedFormData.organization_ems_batch_id !== "" &&
          processedFormData.organization_ems_batch_id != null &&
          processedFormData.organization_ems_batch_id !== undefined
        ) {
          processedFormData.organization_ems_batch_id = Number(
            processedFormData.organization_ems_batch_id
          );
        }
        if (
          processedFormData.organization_ems_batch_class_id !== "" &&
          processedFormData.organization_ems_batch_class_id != null &&
          processedFormData.organization_ems_batch_class_id !== undefined
        ) {
          processedFormData.organization_ems_batch_class_id = Number(
            processedFormData.organization_ems_batch_class_id
          );
        }
      }

      if (baseUrl === "/api/ems/batch-classes") {
        const tsRaw =
          processedFormData.ems_training_space_id !== "" &&
          processedFormData.ems_training_space_id != null &&
          processedFormData.ems_training_space_id !== undefined
            ? processedFormData.ems_training_space_id
            : processedFormData.organization_ems_training_space_id;
        delete processedFormData.organization_ems_training_space_id;
        if (tsRaw !== "" && tsRaw != null && tsRaw !== undefined) {
          processedFormData.ems_training_space_id = Number(tsRaw);
        }
      }

      if (baseUrl === "/api/ems/batches") {
        // Ensure backend receives days as an array.
        if (Array.isArray(processedFormData.batch_days)) {
          processedFormData.batch_days = processedFormData.batch_days
            .map((d) => String(d).trim())
            .filter(Boolean);
        } else if (
          typeof processedFormData.batch_days === "string" &&
          processedFormData.batch_days.trim() !== ""
        ) {
          processedFormData.batch_days = processedFormData.batch_days
            .split(",")
            .map((d) => d.trim())
            .filter(Boolean);
        } else {
          processedFormData.batch_days = [];
        }
        if (processedFormData.batch_start_time != null) {
          processedFormData.batch_start_time = normalizeBatchTimeForInput(
            processedFormData.batch_start_time
          );
        }
        if (processedFormData.batch_end_time != null) {
          processedFormData.batch_end_time = normalizeBatchTimeForInput(
            processedFormData.batch_end_time
          );
        }
      }

      if (baseUrl === "/api/ems/batch-students") {
        const rawMode = processedFormData.mode;
        const modeStr =
          rawMode === null || rawMode === undefined ? "" : String(rawMode).trim();
        if (modeStr) {
          processedFormData.mode =
            modeStr.toLowerCase() === "online"
              ? "Online"
              : modeStr.toLowerCase() === "offline"
                ? "Offline"
                : modeStr;
        }
      }

      if (baseUrl === "/api/ems/lead-stages") {
        // Backend stores lead_status as 1/0, while UI uses Open/Close true/false.
        // Normalize before sending payload.
        const ls = processedFormData.lead_status;
        if (
          ls === true ||
          ls === "true" ||
          ls === 1 ||
          ls === "1"
        ) {
          processedFormData.lead_status = 1;
        } else if (
          ls === false ||
          ls === "false" ||
          ls === 0 ||
          ls === "0"
        ) {
          processedFormData.lead_status = 0;
        }
      }

      if (baseUrl === "/api/ems/leads") {
        const la = processedFormData.lead_activity;
        if (la && typeof la === "object" && !Array.isArray(la)) {
          const normalizedLeadActivity = { ...la };
          if (normalizedLeadActivity.activity_datetime) {
            normalizedLeadActivity.activity_datetime = convertToLaravelDateTime(
              normalizedLeadActivity.activity_datetime
            );
          }
          if (
            normalizedLeadActivity.organization_user_id !== "" &&
            normalizedLeadActivity.organization_user_id != null
          ) {
            normalizedLeadActivity.organization_user_id = Number(
              normalizedLeadActivity.organization_user_id
            );
          }
          delete normalizedLeadActivity.person_type_id;
          processedFormData.lead_activity = normalizedLeadActivity;
        } else {
          processedFormData.lead_activity = null;
        }
      }

      if (baseUrl === "/api/ems/lead-activities") {
        delete processedFormData._lead_datetime;
      }

      if (baseUrl === "/api/ems/demo-sessions") {
        // UI collects IST only; mirror to client fields expected by the API.
        processedFormData.start_time_client =
          processedFormData.start_time_ist ||
          processedFormData.start_time_client ||
          "";
        processedFormData.end_time_client =
          processedFormData.end_time_ist ||
          processedFormData.end_time_client ||
          "";
        if (
          processedFormData.client_timezone === "" ||
          processedFormData.client_timezone == null
        ) {
          processedFormData.client_timezone = "IST";
        }
        // Nested demo participants (full rows for create/update in one request).
        const rawParts = processedFormData.demo_participants;
        if (Array.isArray(rawParts)) {
          processedFormData.demo_participants = rawParts
            .filter((row) => row && row.organization_ems_lead_id)
            .map((row) => {
              const base = {
                organization_ems_lead_id: row.organization_ems_lead_id,
                attendance_status: row.attendance_status ?? "Scheduled",
                student_remarks: row.student_remarks ?? "",
                trainer_remarks: row.trainer_remarks ?? "",
                counsellor_remarks: row.counsellor_remarks ?? "",
              };
              const pid = row.organization_ems_demo_participant_id;
              if (
                pid !== "" &&
                pid !== null &&
                pid !== undefined &&
                !Number.isNaN(Number(pid))
              ) {
                return {
                  ...base,
                  organization_ems_demo_participant_id: Number(pid),
                };
              }
              return base;
            });
        } else {
          processedFormData.demo_participants = [];
        }
      }

      if (baseUrl === "/api/ems/lead-contact-timings") {
        processedFormData.preferred_contact_timezone =
          normalizePreferredContactTimezone(
            processedFormData.preferred_contact_timezone
          );
        const tz = String(
          processedFormData.preferred_contact_timezone || ""
        ).trim();
        if (!tz) {
          toast.error(
            "Preferred contact timezone is required (IANA, e.g. America/Toronto)."
          );
          setBtnLoading(false);
          return;
        }
        if (!isValidIanaTimezone(tz)) {
          toast.error(
            "Invalid timezone. Use a valid IANA name (e.g. America/Toronto, Asia/Dubai)."
          );
          setBtnLoading(false);
          return;
        }
        applyLeadContactTimingIstFields(processedFormData);
        if (
          !processedFormData.morning_ist_time_start ||
          !processedFormData.morning_ist_time_end ||
          !processedFormData.evening_ist_time_start ||
          !processedFormData.evening_ist_time_end
        ) {
          toast.error("Could not convert local times to IST. Check the timezone.");
          setBtnLoading(false);
          return;
        }
      }

      const hasFile = Object.values(processedFormData).some(
        (value) => value instanceof File || value instanceof Blob
      );

      let payload;
      let headers = { Authorization: `Bearer ${token}` };

      if (hasFile) {
        payload = new FormData();

        Object.entries({
          ...processedFormData,
          organization_id: organizationId,
          organization_entity_id,
        }).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            payload.append(key, value);
          }
        });

        if (mode === "edit") {
          payload.append("_method", "PUT");
        }
      } else {
        payload = {
          ...processedFormData,
          organization_id: organizationId,
          organization_entity_id,
        };
      }

      if (mode === "edit") {
        let responseData;
        if (hasFile) {
          const res = await axios.post(`${MAIN_URL}${baseUrl}/${id}`, payload, { headers });
          responseData = res.data;
        } else {
          const res = await axios.put(`${MAIN_URL}${baseUrl}/${id}`, payload, { headers });
          responseData = res.data;
        }
        toast.success(successMessages.update);
        if (typeof afterSuccessfulUpdate === "function") {
          await afterSuccessfulUpdate(id);
        }
        toastLeadAssigneeNotified(responseData, payload, userData);
        refreshNotificationUnreadAfterLeadAssign(baseUrl, organizationId, payload);
      } else {
        const res = await axios.post(`${MAIN_URL}${baseUrl}`, payload, {
          headers,
        });
        const created = res.data?.data ?? res.data;
        const responseData = res.data;

        // Leads: auto-create lead contact timing using country/city when possible.
        if (baseUrl === "/api/ems/leads") {
          const createdLeadId =
            created?.organization_ems_lead_id ??
            created?.id ??
            created?.lead_id;
          if (
            createdLeadId !== undefined &&
            createdLeadId !== null &&
            String(createdLeadId).trim() !== "" &&
            String(createdLeadId) !== "-"
          ) {
            const refDateYmd =
              created?.lead_datetime &&
              String(created.lead_datetime).length >= 10
                ? String(created.lead_datetime).slice(0, 10)
                : undefined;
            const inferredTz = inferPreferredTimezoneFromLead(created);

            const upsertFromLead = () =>
              axios.post(
                `${MAIN_URL}/api/ems/lead-contact-timings/upsert-from-lead/${encodeURIComponent(
                  String(createdLeadId)
                )}`,
                {},
                {
                  headers,
                  params: {
                    organization_id: organizationId,
                    ...(inferredTz && isValidIanaTimezone(inferredTz)
                      ? { preferred_contact_timezone: inferredTz }
                      : {}),
                  },
                }
              );

            try {
              if (inferredTz && isValidIanaTimezone(inferredTz)) {
                const timingBody = {
                  organization_entity_id:
                    created?.organization_entity_id ?? null,
                  organization_ems_lead_id: Number(createdLeadId),
                  preferred_contact_mode: "Call",
                  preferred_contact_timezone: inferredTz,
                };
                applyLeadContactTimingIstFields(timingBody, refDateYmd);
                try {
                  await axios.post(
                    `${MAIN_URL}/api/ems/lead-contact-timings`,
                    {
                      ...timingBody,
                      organization_id: organizationId,
                    },
                    { headers }
                  );
                } catch {
                  await upsertFromLead();
                }
              } else {
                await upsertFromLead();
              }
            } catch (timingErr) {
              console.error(
                "Lead created, but lead contact timing auto-creation failed:",
                timingErr
              );
              toast.error(
                "Lead added, but contact timing was not created automatically."
              );
            }
          }
        }

        toast.success(successMessages.add);
        if (typeof afterSuccessfulCreate === "function") {
          await afterSuccessfulCreate(created);
        }
        toastLeadAssigneeNotified(responseData, payload, userData);
        refreshNotificationUnreadAfterLeadAssign(baseUrl, organizationId, payload);
      }

      navigate(redirectPath);
    } catch (err) {
      if (err.response?.status === 422) {
        const apiErrors = err.response.data?.errors || {};
        const firstMsg = Object.values(apiErrors)
          .flat()
          .find((m) => m != null && String(m).trim() !== "");
        toast.error(
          firstMsg ||
            err.response.data?.message ||
            "Please fix the validation errors."
        );
        setFormErrors(apiErrors);
      } else {
        toast.error(err.response?.data?.message || "Operation failed");
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return {
    id,
    mode,
    formData,
    setFormData,
    formErrors,
    setFormErrors,
    loading,
    btnLoading,
    handleSubmit,
    userData,
    handleChange,
    handleChangeImage,
    handleFileChange,
    handleRemoveFile,
    handlePreview,
  };
}
